// src/pages/user/Dashboard.tsx
import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import ShakaPlayerComponent from "../../components/video/ShakaPlayerComponent";
import { fetchSignedCookie } from "../../utils/videoUtils"; // ← ここだけ！
import Uploader from "../../components/video/Uploader";

type VideoData = {
  id: string;
  title: string;
  key: string;
  isPublic: boolean;
  [key: string]: any;
};

const Dashboard: React.FC = () => {
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // 動画一覧取得
  useEffect(() => {
    const fetchVideos = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const q = query(collection(db, "videos"), where("userId", "==", user.uid));
      const querySnapshot = await getDocs(q);
      const videoList: VideoData[] = querySnapshot.docs.map((docSnap) => {
        const data = docSnap.data() as Omit<VideoData, "id">;
        return {
          id: docSnap.id,
          title: data.title ?? "",
          key: data.key ?? "",
          isPublic: data.isPublic ?? false,
          ...data,
        };
      });
      setVideos(videoList);
    };

    fetchVideos();
  }, []);

  // 動画削除
  const handleDelete = async (video: VideoData) => {
    if (!window.confirm("本当に削除しますか？")) return;
    try {
      await deleteDoc(doc(db, "videos", video.id));
      setVideos((prev) => prev.filter((v) => v.id !== video.id));
    } catch (err) {
      console.error(err);
      alert("削除に失敗しました");
    }
  };

  // 公開/非公開トグル
  const togglePublic = async (video: VideoData) => {
    try {
      const ref = doc(db, "videos", video.id);
      await updateDoc(ref, { isPublic: !video.isPublic });
      setVideos((prev) =>
        prev.map((v) =>
          v.id === video.id ? { ...v, isPublic: !video.isPublic } : v
        )
      );
    } catch (err) {
      console.error(err);
      alert("公開状態の更新に失敗しました");
    }
  };

  // タイトル検索
  const filteredVideos = videos.filter((video) =>
    video.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <h1 className="text-xl font-bold">マイ動画管理</h1>

      <input
        type="text"
        placeholder="動画タイトルで検索"
        className="border p-2 w-full"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <Uploader />

      <div className="grid gap-4">
        {filteredVideos.length === 0 ? (
          <p>動画が見つかりません。</p>
        ) : (
          filteredVideos.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              onDelete={handleDelete}
              onTogglePublic={togglePublic}
            />
          ))
        )}
      </div>
    </div>
  );
};

// 各動画カード＋再生
const VideoCard = ({
  video,
  onDelete,
  onTogglePublic,
}: {
  video: VideoData;
  onDelete: (video: VideoData) => void;
  onTogglePublic: (video: VideoData) => void;
}) => {
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUrl = async () => {
      if (video?.key) {
        setLoading(true);
        setError(null);
        try {
          // m3u8パス生成
          const parts = video.key.split("/");
          const videoIdPart = parts[1];
          const fileBaseName = parts[2]?.replace(/\.[^/.]+$/, "");
          const m3u8Key = `converted/${videoIdPart}/${fileBaseName}/playlist.m3u8`;

          // 署名付きCookieを取得
          await fetchSignedCookie(m3u8Key);

          // CloudFront直URL
          const url = `https://d3ak0hnhe68mgk.cloudfront.net/${m3u8Key}`;
          setPlaybackUrl(url);
        } catch (err: any) {
          setError("Cookie取得または再生URL準備エラー: " + (err.message || String(err)));
        } finally {
          setLoading(false);
        }
      }
    };
    fetchUrl();
  }, [video]);

  return (
    <div className="p-4 border rounded-xl shadow-sm bg-white space-y-2">
      <p className="font-semibold">{video.title}</p>
      {loading ? (
        <p className="text-gray-400">再生URL取得中...</p>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : playbackUrl ? (
        <ShakaPlayerComponent playbackUrl={playbackUrl} />
      ) : (
        <p className="text-gray-400">再生URLを準備中...</p>
      )}
      <div className="flex gap-4 mt-2">
        <button
          onClick={() => onTogglePublic(video)}
          className={`px-3 py-1 rounded ${
            video.isPublic ? "bg-green-500" : "bg-gray-400"
          } text-white`}
        >
          {video.isPublic ? "公開中" : "非公開"}
        </button>
        <button
          onClick={() => onDelete(video)}
          className="bg-red-500 text-white px-3 py-1 rounded"
        >
          削除
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
